import type { GameplayConfig } from "../config/gameplay-config";
import type { RandomSource } from "./seeded-random";
import type { DefeatCombatResolution } from "./size-combat";

export type IgnoredBotDefeatReason =
  | "duplicate-event"
  | "inactive-run"
  | "invalid-event"
  | "invalid-position"
  | "player-defeat"
  | "stale-event"
  | "unknown-bot"
  | "unknown-winner";

export interface BotDefeatCandidate {
  readonly event: DefeatCombatResolution;
  readonly defeatedBot: {
    readonly id: string;
    readonly position: { readonly x: number; readonly y: number };
  } | null;
  readonly winnerExists: boolean;
}

export interface BotDefeatPlan {
  readonly defeatedId: string;
  readonly dropCount: number;
  readonly dropPosition: { readonly x: number; readonly y: number };
  readonly eventKey: string;
  readonly rewardCoins: number;
  readonly runId: number;
  readonly snakesDefeated: number;
  readonly winnerId: string;
}

export interface IgnoredBotDefeatPlan {
  readonly eventKey: string;
  readonly kind: "ignored";
  readonly reason: IgnoredBotDefeatReason;
}

export interface AcceptedBotDefeatPlan extends BotDefeatPlan {
  readonly kind: "accepted";
}

export type BotDefeatPlanResult = AcceptedBotDefeatPlan | IgnoredBotDefeatPlan;

export interface BotDefeatPlanInput extends BotDefeatCandidate {
  readonly activeRunId: number;
  readonly currentSimulationTimeMs: number;
  readonly processedEventKeys: ReadonlySet<string>;
}

type CombatConfig = GameplayConfig["combat"];
type RewardConfig = GameplayConfig["rewards"];

export const botDefeatEventKey = (
  event: Pick<DefeatCombatResolution, "contactId" | "runId">,
): string => `${String(event.runId)}:${event.contactId}`;

const ignored = (
  eventKey: string,
  reason: IgnoredBotDefeatReason,
): IgnoredBotDefeatPlan => ({ eventKey, kind: "ignored", reason });

const isFinitePosition = (position: {
  readonly x: number;
  readonly y: number;
}): boolean => Number.isFinite(position.x) && Number.isFinite(position.y);

const isValidEvent = (event: DefeatCombatResolution): boolean =>
  event.contactId.trim().length > 0 &&
  event.defeatedId.trim().length > 0 &&
  event.winnerId.trim().length > 0 &&
  event.defeatedId !== event.winnerId &&
  Number.isSafeInteger(event.runId) &&
  event.runId > 0 &&
  Number.isFinite(event.observedAtMs) &&
  event.observedAtMs >= 0;

const rewardEntry = (config: RewardConfig): number => {
  const entry = config.entries[0];
  if (entry === undefined) {
    throw new Error("Bot defeat reward configuration is missing");
  }
  return entry.coins;
};

export const createBotDefeatPlan = (
  input: BotDefeatPlanInput,
  combatConfig: CombatConfig,
  rewardConfig: RewardConfig,
  random: RandomSource,
): BotDefeatPlanResult => {
  const { event } = input;
  const eventKey = botDefeatEventKey(event);
  if (!isValidEvent(event)) {
    return ignored(eventKey, "invalid-event");
  }
  if (input.processedEventKeys.has(eventKey)) {
    return ignored(eventKey, "duplicate-event");
  }
  if (!Number.isSafeInteger(input.activeRunId) || input.activeRunId <= 0) {
    return ignored(eventKey, "inactive-run");
  }
  const age = input.currentSimulationTimeMs - event.observedAtMs;
  if (
    event.runId !== input.activeRunId ||
    !Number.isFinite(input.currentSimulationTimeMs) ||
    input.currentSimulationTimeMs < 0 ||
    age < 0 ||
    age > combatConfig.contactMaximumAgeMs
  ) {
    return ignored(eventKey, "stale-event");
  }
  if (event.defeatedId === "player") {
    return ignored(eventKey, "player-defeat");
  }
  if (input.defeatedBot?.id !== event.defeatedId) {
    return ignored(eventKey, "unknown-bot");
  }
  if (!input.winnerExists) {
    return ignored(eventKey, "unknown-winner");
  }
  if (!isFinitePosition(input.defeatedBot.position)) {
    return ignored(eventKey, "invalid-position");
  }
  const playerWon = event.winnerId === "player";
  return {
    defeatedId: event.defeatedId,
    dropCount: random.nextInteger(
      rewardConfig.botFoodDropMinimum,
      rewardConfig.botFoodDropMaximum + 1,
    ),
    dropPosition: { ...input.defeatedBot.position },
    eventKey,
    kind: "accepted",
    rewardCoins:
      playerWon || !rewardConfig.botDefeatPlayerRewardOnly
        ? rewardEntry(rewardConfig)
        : 0,
    runId: event.runId,
    snakesDefeated: playerWon ? 1 : 0,
    winnerId: event.winnerId,
  };
};
